// Teams — Microsoft Teams chat viewer (icon rail + chat list + message stream + composer)
// Converted from dev_projectbuilder TeamsPage.jsx with TypeScript types and
// Cyan Room design system classes.

import { pbAuthedFetch } from '../authHeaders';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ServiceNowTicketModal, type TicketIncident } from '../components/ServiceNowTicketModal';
import { AiSummaryModal } from '../components/AiSummaryModal';
import '../styles/teams-ms.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TeamsChannel {
  id: string;
  name: string;
}

interface TeamsMessage {
  from: string;
  created?: string;
  text?: string;
  subject?: string;
  reactions?: number;
}

interface TeamsOverview {
  ok: boolean;
  team?: { name: string };
  channels?: TeamsChannel[];
  activeChannel?: string;
  messages?: TeamsMessage[];
}

interface IncidentChannelResponse {
  ok: boolean;
  channel: { name: string; description: string } | null;
  messages: TeamsMessage[];
}

interface StaticChatRow {
  key: string;
  label: string;
  tile: 'meeting' | 'pdf' | 'incident';
  unread?: boolean;
}

// ─── Static preview content ────────────────────────────────────────────────
// Rendered only when there is no live Microsoft Graph connection. The chat
// list here (names, header, message thread) mirrors a real Teams chat 1:1 —
// there's no live per-row data behind the other rows, so clicking one only
// changes which row is highlighted (see handleStaticChatClick) rather than
// fabricating message content that was never fetched, same convention the
// channel rail below follows.

const STATIC_QUICK_VIEWS = [{ key: 'drafts', label: 'Drafts' }];

const STATIC_FAVORITES = [{ key: 'self', label: 'Amir Sedrak (You)', initials: 'AS' }];

const STATIC_CHATS: StaticChatRow[] = [
  { key: 'incident-response', label: 'Major Incidents', tile: 'incident' },
  { key: 'itpms-solution-session', label: 'DHS ITPMS Solution Session', tile: 'meeting', unread: true },
  { key: 'tag-up', label: 'CBP Field Lab tag up', tile: 'meeting' },
  { key: 'demo-itpms-prototype', label: 'DHS ITSS Solution Session', tile: 'meeting' },
  { key: 'susan-demo-tag-up', label: 'TECS-FL Modernization Tag Up', tile: 'meeting' },
  { key: 'susan-williamson-dry-run', label: 'SEVIS-FL Dry Run with ICE SEVP', tile: 'meeting' },
  { key: 'adobe-acrobat', label: 'Adobe Acrobat', tile: 'pdf' },
];

const STATIC_DEFAULT_CHAT = 'demo-itpms-prototype';
const MEETING_TITLE = 'DHS ITSS Solution Session';
const MEETING_TABS = ['Chat', 'Shared', 'Recap', 'Q&A', 'Meeting Whiteboard'];
const MEETING_PARTICIPANTS = 5;

interface StaticBubbleGroup {
  kind: 'group';
  from: string;
  avatar: 'ki' | 'mw';
  initials: string;
  time: string;
  bubbles: string[];
  reactions?: { emoji: string; count: number }[];
}
type StaticStreamEntry =
  | { kind: 'system'; text: string }
  | StaticBubbleGroup
  | { kind: 'ended'; time: string; duration: string };

const STATIC_STREAM: StaticStreamEntry[] = [
  { kind: 'system', text: 'Amir Sedrak was invited to the meeting.' },
  {
    kind: 'group',
    from: 'Karen Ibarra',
    avatar: 'ki',
    initials: 'KI',
    time: 'Friday 11:34 AM',
    bubbles: [
      'ITSS Status -\n- CBP Network Segmentation Review\n- FEMA Disaster Recovery Site Sync\n- TSA Checkpoint Kiosk Refresh',
      '• DHS Trusted Internet Connection (TIC) 3.0 Migration',
      '- CISA Vulnerability Scan Remediation\n- Identity Credential and Access Mgmt (ICAM) - Phase 2\n-',
    ],
  },
  {
    kind: 'group',
    from: 'Marcus Whitfield',
    avatar: 'mw',
    initials: 'MW',
    time: 'Friday 11:41 AM',
    bubbles: ['brb - CBP field issue', 'ServiceNow is DHS OCIO’s primary enterprise platform'],
    reactions: [{ emoji: '👍', count: 1 }],
  },
  {
    kind: 'group',
    from: 'Marcus Whitfield',
    avatar: 'mw',
    initials: 'MW',
    time: 'Friday 12:05 PM',
    bubbles: ['heard', 'back'],
  },
  { kind: 'ended', time: 'Friday 12:20 PM', duration: '1h 52m 11s' },
];

// ─── Icons ────────────────────────────────────────────────────────────────────

function IcoBell() {
  return (
    <svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M4 6.5a4 4 0 0 1 8 0c0 3 1 4 1.5 4.5H2.5C3 10.5 4 9.5 4 6.5z" strokeLinejoin="round" />
      <path d="M6.5 13.5a1.5 1.5 0 0 0 3 0" strokeLinecap="round" />
    </svg>
  );
}
function IcoChatBubble({ active = false }: { active?: boolean }) {
  if (active) {
    return (
      <svg width="17" height="17" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M2 3.5h12v7H6.5L3.5 13V10.5H2z" />
        <path d="M4.5 6.2h7M4.5 8.5h4.5" stroke="var(--ms-rail-bg)" strokeWidth="1.1" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M2 3.5h12v7H6.5L3.5 13V10.5H2z" strokeLinejoin="round" />
    </svg>
  );
}
function IcoCalendarRail() {
  return (
    <svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <rect x="2" y="3" width="12" height="11" rx="1.5" />
      <path d="M2 6.5h12M5 1.5v3M11 1.5v3" strokeLinecap="round" />
    </svg>
  );
}
function IcoPhone() {
  return (
    <svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M3.5 2.5c.5-.3 1.4-.4 1.7.2l1 2c.2.5 0 1-.4 1.3l-.9.7c.6 1.4 1.7 2.5 3.1 3.1l.7-.9c.3-.4.8-.6 1.3-.4l2 1c.6.3.5 1.2.2 1.7-.5.8-1.5 1.3-2.4 1.1-3.2-.6-6-3.4-6.6-6.6-.2-.9.3-1.9 1.1-2.4z" strokeLinejoin="round" />
    </svg>
  );
}
function IcoCloud() {
  return (
    <svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M4.5 12A3 3 0 0 1 4 6a4 4 0 0 1 7.7-1A3.2 3.2 0 0 1 11.5 12h-7z" strokeLinejoin="round" />
    </svg>
  );
}
function IcoMore() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <circle cx="3.5" cy="8" r="1.3" />
      <circle cx="8" cy="8" r="1.3" />
      <circle cx="12.5" cy="8" r="1.3" />
    </svg>
  );
}
function IcoPlus() {
  return (
    <svg width="17" height="17" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path d="M8 2.5v11M2.5 8h11" strokeLinecap="round" />
    </svg>
  );
}
function IcoSearch() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.3 10.3 14 14" strokeLinecap="round" />
    </svg>
  );
}
function IcoCompose() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M10.5 2.5 13.5 5.5 5 14H2v-3z" strokeLinejoin="round" />
    </svg>
  );
}
function IcoChevronDown() {
  return (
    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M3.5 6 8 10.5 12.5 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IcoDraft() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M4 2h6l3 3v9H4z" strokeLinejoin="round" />
      <path d="M6 8.5 9 8" strokeLinecap="round" />
    </svg>
  );
}
function IcoPdf() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="1.3" aria-hidden="true">
      <path d="M4 2h5l3 3v9H4z" strokeLinejoin="round" />
    </svg>
  );
}
function IcoAlertTile() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M8 2 14.5 13H1.5z" strokeLinejoin="round" />
      <path d="M8 6.5v3M8 11.5v.01" strokeLinecap="round" />
    </svg>
  );
}
function IcoSpark() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 1.5l1.5 4 4 1.5-4 1.5L8 12.5 6.5 8.5 2.5 7l4-1.5z" />
    </svg>
  );
}
function IcoMeetingTile() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
      <rect x="2.5" y="3" width="11" height="10.5" rx="1.5" />
      <path d="M2.5 6h11" />
      <circle cx="5.5" cy="8.5" r=".6" fill="currentColor" stroke="none" />
      <circle cx="8" cy="8.5" r=".6" fill="currentColor" stroke="none" />
      <circle cx="10.5" cy="8.5" r=".6" fill="currentColor" stroke="none" />
      <circle cx="5.5" cy="11" r=".6" fill="currentColor" stroke="none" />
      <circle cx="8" cy="11" r=".6" fill="currentColor" stroke="none" />
    </svg>
  );
}
function IcoPeople() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <circle cx="6" cy="5.5" r="2.2" />
      <path d="M1.7 13c.6-2.3 2.2-3.6 4.3-3.6s3.7 1.3 4.3 3.6" strokeLinecap="round" />
      <path d="M10.3 3.3a2.2 2.2 0 0 1 0 4.3M12 13c-.3-1.5-.9-2.6-2-3.3" strokeLinecap="round" />
    </svg>
  );
}
function IcoListView() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <path d="M2 4h12M2 8h12M2 12h12" strokeLinecap="round" />
    </svg>
  );
}
function IcoExpand() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <path d="M6 2H2v4M10 2h4v4M6 14H2v-4M10 14h4v-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IcoFormat() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M3 12 6.5 3h1L11 12M4.2 9h4.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12.5 13h2" strokeLinecap="round" />
    </svg>
  );
}
function IcoEmoji() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <circle cx="8" cy="8" r="6" />
      <circle cx="5.7" cy="6.7" r=".6" fill="currentColor" stroke="none" />
      <circle cx="10.3" cy="6.7" r=".6" fill="currentColor" stroke="none" />
      <path d="M5.3 9.5c.6.9 1.6 1.5 2.7 1.5s2.1-.6 2.7-1.5" strokeLinecap="round" />
    </svg>
  );
}
function IcoAttach() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M10.5 4.2 5.7 9a2 2 0 0 0 2.8 2.8l4.3-4.3a3.3 3.3 0 0 0-4.7-4.6L3.8 7.1a4.6 4.6 0 0 0 6.5 6.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IcoSticker() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <rect x="2" y="2" width="12" height="12" rx="3" />
      <circle cx="6.2" cy="6.5" r=".6" fill="currentColor" stroke="none" />
      <circle cx="9.8" cy="6.5" r=".6" fill="currentColor" stroke="none" />
      <path d="M5.7 9.3c.6.7 1.4 1.1 2.3 1.1s1.7-.4 2.3-1.1" strokeLinecap="round" />
    </svg>
  );
}
function IcoAddReaction() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <circle cx="7" cy="7" r="5.3" />
      <path d="M5 8c.5.7 1.2 1.1 2 1.1S8.5 8.7 9 8" strokeLinecap="round" />
      <path d="M13 3.5v3M11.5 5h3" strokeLinecap="round" />
    </svg>
  );
}
function IcoInvite() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <rect x="2" y="3" width="12" height="10" rx="1.5" />
      <path d="M2 6.5h12M10.5 1.5v3" strokeLinecap="round" />
      <path d="M8.5 9.5h3M10 8v3" strokeLinecap="round" />
    </svg>
  );
}
function IcoVideoOff() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <rect x="1.5" y="4.5" width="9" height="7" rx="1" />
      <path d="M10.5 7l3-2v6l-3-2" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function escHtml(s: string | null | undefined): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c] || c,
  );
}

function initials(name: string | null | undefined): string {
  if (!name) return '—';
  const p = name.trim().split(/\s+/);
  return (((p[0] || '')[0] || '') + ((p[p.length - 1] || '')[0] || '')).toUpperCase() || '—';
}

function msgTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const t = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return (d.toDateString() === now.toDateString() ? 'Today' : DOW[d.getDay()]) + ' ' + t;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TeamsPage() {
  const [activeChannelId, setActiveChannelId] = useState<string | null>(null);
  // No live per-row data exists for the other static rows (see the comment
  // above STATIC_CHATS) — this only tracks which row is visually selected.
  const [staticActiveChat, setStaticActiveChat] = useState(STATIC_DEFAULT_CHAT);
  // Which of the open chat's header sub-tabs (Chat/Shared/Recap/Q&A/Meeting
  // Whiteboard) is selected. Resets to "Chat" whenever a different chat row
  // is opened, same as real Teams.
  const [activeMeetingTab, setActiveMeetingTab] = useState<string>('Chat');
  const [activeChip, setActiveChip] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const msgsRef = useRef<HTMLDivElement>(null);

  // Triage flow (Major Incidents channel → "Triage" → ServiceNow ticket
  // popup). Local/decorative only — no backend call. `token` guards against
  // a stale loading timer resolving after a second, faster click.
  const [triage, setTriage] = useState<{ phase: 'loading' | 'ticket'; incident: TicketIncident; step: number } | null>(null);
  const triageTokenRef = useRef(0);
  const startTriage = useCallback((incident: TicketIncident) => {
    const token = ++triageTokenRef.current;
    setTriage({ phase: 'loading', incident, step: 0 });
    setTimeout(() => {
      if (triageTokenRef.current === token) setTriage({ phase: 'loading', incident, step: 1 });
    }, 700);
    setTimeout(() => {
      if (triageTokenRef.current === token) setTriage({ phase: 'ticket', incident, step: 1 });
    }, 1500);
  }, []);
  const closeTriage = useCallback(() => {
    triageTokenRef.current++;
    setTriage(null);
  }, []);

  // "Summarize" (AiSummaryModal) — works on whatever channel/chat is
  // currently open (live Graph messages, Major Incidents, or a static demo
  // chat). Its own Triage button hands off to the ServiceNow ticket popup,
  // seeded with the generated summary as work notes.
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  const pendingSummaryRef = useRef<string>('');

  const { data: live } = useQuery<TeamsOverview>({
    queryKey: ['teams-overview', activeChannelId],
    queryFn: async () => {
      const params = activeChannelId ? `?channelId=${encodeURIComponent(activeChannelId)}` : '';
      const res = await pbAuthedFetch(`/api/integrations/teams/overview${params}`);
      if (res.status === 400) return null as unknown as TeamsOverview;
      const json = await res.json();
      if (!res.ok || !json.ok) return null as unknown as TeamsOverview;
      return json as TeamsOverview;
    },
    enabled: true,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Local mirror of every critical incident's Teams card (real or
  // simulated via OutagesPage's "Simulate Outage" button) — see
  // teamsWebhook.js's postToTeams(). Polled independently of the live
  // Microsoft Graph query above so incident alerts stay visible even
  // without a connected Microsoft account.
  const { data: incidentChannel } = useQuery<IncidentChannelResponse>({
    queryKey: ['teams-incident-channel'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/integrations/teams/incident-channel');
      const json = await res.json();
      return json as IncidentChannelResponse;
    },
    refetchInterval: 5000,
    refetchOnWindowFocus: false,
  });

  // Scroll to bottom on new messages
  useEffect(() => {
    if (live && msgsRef.current) {
      msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
    }
  }, [live]);

  const handleChannelClick = useCallback((channelId: string) => {
    setActiveChannelId(channelId);
  }, []);

  const handleStaticChatClick = useCallback((key: string) => {
    setStaticActiveChat(key);
    setActiveMeetingTab('Chat');
  }, []);

  const toggleChip = useCallback((key: string) => {
    setActiveChip((prev) => (prev === key ? null : key));
  }, []);

  const channels = live?.channels ?? [];
  const activeCh = channels.find((c) => c.id === live?.activeChannel) ?? null;
  const teamName = live?.team?.name ?? 'Team';
  const incidentMessages = incidentChannel?.messages ?? [];
  const isIncidentsActive = !live && staticActiveChat === 'incident-response';
  const hasIncidentAlerts = incidentMessages.length > 0;
  // Only the seeded "DHS ITSS Solution Session" row has real transcript
  // content (STATIC_STREAM) and a meeting header (tabs/Join/participants);
  // every other static row is a plain chat with no fabricated messages, so
  // it opens to just its own title and an empty state.
  const selectedStaticChat = STATIC_CHATS.find((c) => c.key === staticActiveChat) ?? null;
  const isMeetingChat = !live && staticActiveChat === STATIC_DEFAULT_CHAT;

  // Live-driven header/subtitle/placeholder strings
  const chTitle = live
    ? `${teamName} · # ${activeCh?.name ?? ''}`
    : isIncidentsActive
      ? (incidentChannel?.channel?.name ?? 'Major Incidents')
      : (selectedStaticChat?.label ?? MEETING_TITLE);
  const chSub = live
    ? `${channels.length} channels · live via Microsoft Graph`
    : isIncidentsActive
      ? (incidentChannel?.channel?.description ?? 'Bridge coordination and status updates')
      : `${STATIC_CHATS.length} people`;
  const pageSub = live
    ? `Microsoft Teams · ${channels.length} channels · ${live.team?.name ?? ''}`
    : 'Microsoft Teams · 6 chats · 1 unread';
  const replyPh = activeCh ? `Reply to # ${activeCh.name}…` : 'Type a message';

  const avc = ['nv', 'gd', 'tl', 'gn', 'st', 'pv'];
  const liveMsgs = live?.messages ?? [];
  const chips = ['Unread', 'Channels', 'Chats', 'Meeting chats'];

  // Static/demo chat list has no separate "channel" entries (see STATIC_CHATS
  // comment above) — only "Unread" and "Meeting chats" map onto real data
  // here, "Chats" is everything that isn't a meeting, and "Channels" has
  // nothing to show in this static preview.
  const visibleStaticChats = STATIC_CHATS.filter((c) => {
    if (activeChip === 'Unread') {
      return c.key === 'incident-response' ? hasIncidentAlerts : !!c.unread;
    }
    if (activeChip === 'Meeting chats') return c.tile === 'meeting';
    if (activeChip === 'Chats') return c.tile !== 'meeting';
    if (activeChip === 'Channels') return false;
    return true;
  });

  function buildChannelSummaryPrompt(): string {
    let lines: string[];
    if (live) {
      lines = liveMsgs.map((m) => `${m.from}: ${(m.text || m.subject || '').replace(/\n/g, ' ')}`);
    } else if (isIncidentsActive) {
      lines = incidentMessages.map((m) => `${m.from}: ${(m.text || '').replace(/\n/g, ' ')}`);
    } else if (isMeetingChat) {
      lines = STATIC_STREAM.filter((e): e is StaticBubbleGroup => e.kind === 'group')
        .flatMap((e) => e.bubbles.map((b) => `${e.from}: ${b.replace(/\n/g, ' ')}`));
    } else {
      lines = [];
    }
    const convo = lines.slice(-30).join('\n') || '(no messages)';
    return [
      `Summarize this "${chTitle}" Teams conversation for someone catching up.`,
      'Cover: what is being discussed, any decisions or action items, and anything that looks urgent or needs follow-up.',
      'Keep it to a short paragraph plus a bullet list of action items if there are any.',
      '',
      'Conversation:',
      convo,
    ].join('\n');
  }

  return (
    <div className="page active" id="page-teams">
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">{pageSub}</p>
            <h1 className="pagehead__title" role="heading" aria-level={1}>
              Teams
            </h1>
          </div>
        </div>
      </div>

      <div className="tms-wrap">
        {/* Teams app icon rail */}
        <div className="tms-rail" role="navigation" aria-label="Teams">
          <button className="tms-rail-btn" title="Activity" aria-label="Activity">
            <IcoBell />
          </button>
          <button className="tms-rail-btn active" title="Chat" aria-label="Chat">
            <IcoChatBubble active />
            <span className="tms-rail-badge">1</span>
          </button>
          <button className="tms-rail-btn" title="Calendar" aria-label="Calendar">
            <IcoCalendarRail />
          </button>
          <button className="tms-rail-btn" title="Calls" aria-label="Calls">
            <IcoPhone />
          </button>
          <button className="tms-rail-btn" title="OneDrive" aria-label="OneDrive">
            <IcoCloud />
          </button>
          <div className="tms-rail-gap" />
          <button className="tms-rail-btn" title="More apps" aria-label="More apps">
            <IcoMore />
          </button>
          <button className="tms-rail-btn" title="Add app" aria-label="Add app">
            <IcoPlus />
          </button>
        </div>

        {/* Chat list */}
        <div className="tms-sb">
          <div className="tms-sb-h">
            <h2>Chat</h2>
            <div className="tms-sb-h-actions">
              <button className="nav-btn" title="More options" aria-label="More options">
                <IcoMore />
              </button>
              <button className="nav-btn" title="Search" aria-label="Search">
                <IcoSearch />
              </button>
              <button className="nav-btn" title="New chat" aria-label="New chat">
                <IcoCompose />
              </button>
            </div>
          </div>

          <div className="tms-chips" role="group" aria-label="Chat filters">
            {chips.map((c) => (
              <button
                key={c}
                type="button"
                className="tms-chip"
                aria-pressed={activeChip === c}
                onClick={() => toggleChip(c)}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="tms-sb-list">
            {live ? (
              <div className="tms-team">
                <div className="tms-team-n"><IcoChevronDown />{teamName}</div>
                {channels.map((c) => (
                  <div
                    key={c.id}
                    className={`tms-ch${c.id === live.activeChannel ? ' active' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleChannelClick(c.id)}
                  >
                    {c.name}
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="tms-team">
                  <div className="tms-team-n"><IcoChevronDown />Quick views</div>
                  {STATIC_QUICK_VIEWS.map((qv) => (
                    <div className="tms-row" key={qv.key}>
                      <IcoDraft />
                      <span className="tms-row-label">{qv.label}</span>
                    </div>
                  ))}
                </div>

                <div className="tms-team">
                  <div className="tms-team-n"><IcoChevronDown />Favorites</div>
                  {STATIC_FAVORITES.map((f) => (
                    <div className="tms-row" key={f.key}>
                      <span className="tms-av-round">
                        {f.initials}
                        <span className="tms-status-dot" />
                      </span>
                      <span className="tms-row-label">{f.label}</span>
                    </div>
                  ))}
                </div>

                <div className="tms-team">
                  <div className="tms-team-n"><IcoChevronDown />Chats</div>
                  {visibleStaticChats.length === 0 && (
                    <div className="tms-row" style={{ cursor: 'default', opacity: 0.6 }}>
                      <span className="tms-row-label">No {activeChip?.toLowerCase()} to show</span>
                    </div>
                  )}
                  {visibleStaticChats.map((c) => {
                    const isActive = c.key === staticActiveChat;
                    const unread = c.key === 'incident-response' ? hasIncidentAlerts : c.unread;
                    return (
                      <div
                        key={c.key}
                        className={`tms-row${isActive ? ' active' : ''}${unread && !isActive ? ' unread' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleStaticChatClick(c.key)}
                      >
                        <span className={`tms-tile ${c.tile}`}>
                          {c.tile === 'pdf' ? <IcoPdf /> : c.tile === 'incident' ? <IcoAlertTile /> : <IcoMeetingTile />}
                        </span>
                        <span className="tms-row-label">{c.label}</span>
                        {unread && !isActive && <span className="tms-row-dot" aria-label="Unread" />}
                      </div>
                    );
                  })}
                </div>

                <div className="tms-team">
                  <div className="tms-team-n"><IcoChevronDown />Teams and channels</div>
                  <button type="button" className="tms-see-all">
                    See all your teams
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Message pane */}
        <div className="tms-main">
          <div className="tms-ch-h">
            <div className="tms-ch-h-l">
              <div className="tms-mh-tile">
                {live ? teamName.slice(0, 2).toUpperCase() : isIncidentsActive ? <IcoAlertTile /> : selectedStaticChat?.tile === 'pdf' ? <IcoPdf /> : <IcoMeetingTile />}
              </div>
              {!live && !isIncidentsActive && (
                <div>
                  <div className="tms-ch-h-t">{chTitle}</div>
                </div>
              )}
              {(live || isIncidentsActive) && (
                <div>
                  <div className="tms-ch-h-t">{chTitle}</div>
                  <div className="tms-ch-h-s">{chSub}</div>
                </div>
              )}
            </div>

            {isMeetingChat && (
              <nav className="tms-mh-tabs" aria-label="Meeting tabs">
                {MEETING_TABS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`tms-mh-tab${activeMeetingTab === t ? ' active' : ''}`}
                    aria-pressed={activeMeetingTab === t}
                    onClick={() => setActiveMeetingTab(t)}
                  >
                    {t}
                  </button>
                ))}
                <button type="button" className="tms-mh-tab-add" title="Add a tab" aria-label="Add a tab">
                  <IcoPlus />
                </button>
              </nav>
            )}

            <div className="tms-ch-h-actions">
              {isMeetingChat && (
                <>
                  <button type="button" className="tms-mh-join">
                    Join
                  </button>
                  <button type="button" className="tms-mh-participants" title="Participants" aria-label="Participants">
                    <IcoPeople />
                    {MEETING_PARTICIPANTS}
                  </button>
                  <button className="nav-btn" title="List view" aria-label="List view">
                    <IcoListView />
                  </button>
                </>
              )}
              <button
                className="nav-btn tms-ai-summarize-btn"
                title="Summarize with AI"
                aria-label="Summarize with AI"
                onClick={() => setAiSummaryOpen(true)}
              >
                <IcoSpark />
              </button>
              <button className="nav-btn" title="Search" aria-label="Search">
                <IcoSearch />
              </button>
              {isMeetingChat && (
                <button className="nav-btn" title="Expand" aria-label="Expand">
                  <IcoExpand />
                </button>
              )}
              <button className="nav-btn" title="More options" aria-label="More options">
                <IcoMore />
              </button>
            </div>
          </div>

          {live || isIncidentsActive ? (
            <div className="tms-msgs" ref={msgsRef}>
              {(live ? liveMsgs : incidentMessages).length === 0 && (
                <div className="tms-day-sep">
                  <span>{isIncidentsActive ? 'No incident alerts yet — Simulate Outage on the Outages page to see one here.' : 'No messages in this channel'}</span>
                </div>
              )}
              {isIncidentsActive
                ? incidentMessages.map((m, i) => {
                    const lines = (m.text || '').split('\n').filter(Boolean);
                    const title = lines[0] || '';
                    const facts = lines.slice(1);
                    const isResolved = facts.some((l) => l.toLowerCase() === 'state: resolved');
                    const factsRecord: Record<string, string> = {};
                    for (const line of facts) {
                      const idx = line.indexOf(': ');
                      if (idx !== -1) factsRecord[line.slice(0, idx)] = line.slice(idx + 2);
                    }
                    const cleanFact = (key: string) => (factsRecord[key] && factsRecord[key] !== '—' ? factsRecord[key] : undefined);
                    const ticketIncident: TicketIncident = {
                      title,
                      createdAt: m.created,
                      isResolved,
                      configurationItem: cleanFact('Service'),
                      location: cleanFact('Site'),
                      workNotes: cleanFact('Status'),
                      activityNote: cleanFact('Workaround'),
                    };
                    return (
                      <div className="tms-msg" key={i}>
                        <div className="tms-av incident">{initials(m.from)}</div>
                        <div className="tms-msg-body">
                          <div className="tms-msg-h">
                            <span className="tms-msg-from">{m.from}</span>
                            <span className="tms-msg-time">{msgTime(m.created)}</span>
                          </div>
                          <div className={`tms-alert-card${isResolved ? ' tms-alert-card--resolved' : ''}`}>
                            <div className="tms-alert-banner">
                              <span aria-hidden="true">{isResolved ? '✅' : '⚠️'}</span>
                              <span>{isResolved ? 'INCIDENT RESOLVED' : 'CRITICAL INCIDENT'}</span>
                            </div>
                            <div className="tms-alert-body">
                              <div className="tms-alert-title">{title}</div>
                              {facts.length > 0 && (
                                <div className="tms-alert-facts">
                                  {facts.map((line, fi) => {
                                    const idx = line.indexOf(': ');
                                    if (idx === -1) return <div key={fi} className="tms-alert-fact-line">{line}</div>;
                                    return (
                                      <div key={fi} className="tms-alert-fact">
                                        <span className="tms-alert-fact-label">{line.slice(0, idx)}:</span>
                                        <span className="tms-alert-fact-value">{line.slice(idx + 2)}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="tms-triage-btn"
                            onClick={() => startTriage(ticketIncident)}
                          >
                            <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M9 1 3 9h4l-1 6 7-9H9z" /></svg>
                            Triage
                          </button>
                        </div>
                      </div>
                    );
                  })
                : liveMsgs.map((m, i) => (
                    <div className="tms-msg" key={i}>
                      <div className={`tms-av ${avc[i % avc.length]}`}>{initials(m.from)}</div>
                      <div
                        className="tms-msg-body"
                        dangerouslySetInnerHTML={{
                          __html:
                            `<div class="tms-msg-h"><span class="tms-msg-from">${escHtml(m.from)}</span><span class="tms-msg-time">${escHtml(msgTime(m.created))}</span></div>` +
                            `<div class="tms-msg-text"><p>${escHtml(m.text || m.subject || '').replace(/\n/g, '<br>')}</p></div>` +
                            (m.reactions ? `<div class="tms-msg-react"><span class="tms-react">♥ ${m.reactions}</span></div>` : ''),
                        }}
                      />
                    </div>
                  ))}
            </div>
          ) : !isMeetingChat ? (
            <div className="tms-msgs">
              <div className="tms-day-sep">
                <span>No messages in this chat</span>
              </div>
            </div>
          ) : activeMeetingTab !== 'Chat' ? (
            <div className="tms-msgs">
              <div className="tms-day-sep">
                <span>No {activeMeetingTab.toLowerCase()} content for this meeting</span>
              </div>
            </div>
          ) : (
            <div className="tms-msgs">
              {STATIC_STREAM.map((entry, i) => {
                if (entry.kind === 'system') {
                  return (
                    <div className="tms-sysmsg" key={i}>
                      <IcoInvite />
                      <span>{entry.text}</span>
                    </div>
                  );
                }
                if (entry.kind === 'ended') {
                  return (
                    <div className="tms-meeting-end" key={i}>
                      <IcoVideoOff />
                      <span>
                        {entry.time} &nbsp; Meeting ended: <strong>{entry.duration}</strong>
                      </span>
                    </div>
                  );
                }
                return (
                  <div className="tms-msg" key={i}>
                    <div className={`tms-av ${entry.avatar}`}>
                      {entry.initials}
                      <span className="tms-status-dot away" />
                    </div>
                    <div className="tms-msg-body">
                      <div className="tms-msg-h">
                        <span className="tms-msg-from">{entry.from}</span>
                        <span className="tms-msg-time">{entry.time}</span>
                      </div>
                      <div className="tms-bubbles">
                        {entry.bubbles.map((b, bi) => (
                          <div className="tms-bubble" key={bi}>
                            {b}
                          </div>
                        ))}
                      </div>
                      {entry.reactions && entry.reactions.length > 0 && (
                        <div className="tms-msg-react">
                          {entry.reactions.map((r, ri) => (
                            <span className="tms-react reacted" key={ri}>
                              {r.emoji} {r.count}
                            </span>
                          ))}
                          <span className="tms-react-add" title="Add a reaction" aria-label="Add a reaction">
                            <IcoAddReaction />
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="tms-input box__foot">
            <div className="tms-input-wrap">
              <textarea
                placeholder={replyPh}
                rows={1}
                aria-label={replyPh}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <div className="tms-input-tools">
                <button className="nav-btn" title="Format" aria-label="Format">
                  <IcoFormat />
                </button>
                <button className="nav-btn" title="Emoji" aria-label="Emoji">
                  <IcoEmoji />
                </button>
                <button className="nav-btn" title="Attach" aria-label="Attach">
                  <IcoAttach />
                </button>
                <button className="nav-btn" title="Sticker" aria-label="Sticker">
                  <IcoSticker />
                </button>
                <button className="nav-btn" title="Add" aria-label="Add">
                  <IcoPlus />
                </button>
              </div>
              <button
                className={`tms-send${draft.trim() ? ' ready' : ''}`}
                title="Send"
                aria-label="Send message"
                disabled={!draft.trim()}
              >
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path
                    d="M2 7l10-5-3 12-3-5-4-2z"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinejoin="round"
                    fill="currentColor"
                    fillOpacity=".25"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {triage?.phase === 'loading' && (
        <div className="sn-overlay">
          <div className="sn-loading-card">
            <div className="sn-loading-steps">
              {['Analyzing incident', 'Preparing ServiceNow ticket'].map((label, i) => (
                <div key={label} style={{ display: 'contents' }}>
                  {i > 0 && <div className="sn-loading-line" />}
                  <div className={`sn-loading-step${i < triage.step ? ' is-done' : i === triage.step ? ' is-active' : ''}`}>
                    <div className="sn-loading-dot">
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3.5 8.5 6.5 11.5 12.5 4.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </div>
                    <div className="sn-loading-label">{label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {triage?.phase === 'ticket' && (
        <ServiceNowTicketModal incident={triage.incident} onClose={closeTriage} />
      )}

      {aiSummaryOpen && (
        <AiSummaryModal
          title={chTitle}
          subtitle={chSub}
          prompt={buildChannelSummaryPrompt()}
          onSummaryReady={(summary) => { pendingSummaryRef.current = summary; }}
          onClose={() => setAiSummaryOpen(false)}
          onTriage={() => {
            setAiSummaryOpen(false);
            triageTokenRef.current++;
            setTriage({
              phase: 'ticket',
              step: 1,
              incident: {
                title: `Channel discussion: ${chTitle}`,
                isResolved: false,
                category: 'Team Discussion',
                caller: 'Atlas Channel Bot',
                openedBy: 'Atlas Channel Bot',
                impact: '3 - Low',
                urgency: '3 - Low',
                priority: '4 - Low',
                assignmentGroup: 'Service Desk',
                workNotes: pendingSummaryRef.current,
              },
            });
          }}
        />
      )}
    </div>
  );
}
